import React from 'react';
import PropTypes from 'prop-types';
import styles from './treatmentCardMsg.module.css';

export const TreatmentCardMsg = ({ content }) => {
  const {
    title,
    subtitle,
    category,
    administrationRoute,
    settings,
    pointsToDiscuss,
    description,
    drugCombination,
    officialDrugInfoLink,
    preference,
    groups,
  } = content;

  const route = administrationRoute || 'Not specified';
  const settingsList = Array.isArray(settings) ? settings.filter(Boolean) : [];
  const hasSettings = settingsList.length > 0;

  const points = Array.isArray(pointsToDiscuss) ? pointsToDiscuss.filter(Boolean) : [];
  const hasPoints = points.length > 0;

  const comboList = Array.isArray(drugCombination) ? drugCombination.filter(Boolean) : [];
  const hasCombination = comboList.length > 0;

  const metaGroups = Array.isArray(groups) ? groups.filter(Boolean) : [];
  const hasMeta = Boolean(preference) || metaGroups.length > 0 || Boolean(officialDrugInfoLink);

  return (
    <div className={styles.card}>
      <div className={styles.header}>
        <h3 className={styles.title}>{title || 'Treatment'}</h3>
        {category ? (
          <span className={styles.categoryChip}>{category}</span>
        ) : null}
      </div>

      {subtitle ? <p className={styles.subtitle}>{subtitle}</p> : null}

      {hasCombination ? (
        <p className={styles.subtitle}>{`Combined with: ${comboList.join(', ')}`}</p>
      ) : null}

      {description ? (
        <div className={styles.section}>
          <strong className={styles.sectionTitle}>Overview</strong>
          <p className={styles.overviewText}>{description}</p>
        </div>
      ) : null}

      <table className={styles.table}>
        <tbody>
          <tr className={styles.row}>
            <td className={styles.labelCell}>How it&apos;s Given:</td>
            <td className={styles.valueCell}>{route}</td>
          </tr>
          {hasSettings ? (
            <tr className={styles.row}>
              <td className={styles.labelCell}>Settings:</td>
              <td className={styles.valueCell}>{settingsList.join(', ')}</td>
            </tr>
          ) : null}
        </tbody>
      </table>

      {hasPoints ? (
        <div className={styles.section}>
          <strong className={styles.sectionTitle}>Key Discussion Points</strong>
          <ul className={styles.list}>
            {points.map((point, idx) => (
              <li key={idx}>{point}</li>
            ))}
          </ul>
        </div>
      ) : null}

      {hasMeta ? (
        <div className={styles.metaSection}>
          {preference ? (
            <div className={styles.preferenceRow}>
              <strong className={styles.metaLabel}>Preference:</strong>
              <span className={styles.metaValue}>{preference}</span>
            </div>
          ) : null}

          {metaGroups.length > 0 ? (
            <div className={styles.groups}>
              {metaGroups.map((group, idx) => (
                <span key={idx} className={styles.groupChip}>{group}</span>
              ))}
            </div>
          ) : null}

          {officialDrugInfoLink ? (
            <div className={styles.linkRow}>
              <a
                href={officialDrugInfoLink}
                target="_blank"
                rel="noopener noreferrer"
                className={styles.link}
              >
                Learn more from the official source
              </a>
            </div>
          ) : null}
        </div>
      ) : null}

      <p className={styles.disclaimer}>
        This is for informational purposes. Always consult your healthcare provider for medical advice.
      </p>
    </div>
  );
};

TreatmentCardMsg.propTypes = {
  content: PropTypes.shape({
    id: PropTypes.string,
    title: PropTypes.string.isRequired,
    subtitle: PropTypes.string,
    category: PropTypes.string,
    administrationRoute: PropTypes.string,
    settings: PropTypes.arrayOf(PropTypes.string),
    pointsToDiscuss: PropTypes.arrayOf(PropTypes.string),
    description: PropTypes.string,
    drugCombination: PropTypes.arrayOf(PropTypes.string),
    officialDrugInfoLink: PropTypes.string,
    preference: PropTypes.string,
    groups: PropTypes.arrayOf(PropTypes.string),
  }).isRequired,
};
